// File: src/components/layouts/navigation/Footer/Footer.tsx

import React from "react";
import styled, { css } from "styled-components";
import { Typography } from "../../../general/Typography";
import { Flex } from "../../wrappers/Flex";
import { Divider } from "../../wrappers/Divider";
import { Button } from "../../../general/Button";

interface FooterProps {
  logo?: string;
  title?: string;
  navigation?: { label: string; path: string; external?: boolean }[];
  socialLinks?: { icon: React.ReactNode; url: string }[];
  legalLinks?: { label: string; path: string; external?: boolean }[];
  compact?: boolean;
  showCopyright?: boolean;
  backgroundColor?: string; // ✅ Support for transparent/custom bg
  as?: React.ElementType;
}

const StyledFooter = styled.footer<{ backgroundColor?: string }>`
  background: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
`;

const FooterSection = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  > div {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-wrap: wrap;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};

    > div {
      justify-content: center;
      width: 100%;
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};

  button {
    color: ${({ theme }) => theme.colors.textSecondary};

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const CopyrightText = styled(Typography)`
  white-space: nowrap;
  
`;

export const Footer: React.FC<FooterProps> = ({
  logo,
  title = "Your Brand",
  socialLinks = [],
  legalLinks = [],
  compact = false,
  showCopyright = true,
  backgroundColor,
  as: LinkComponent = "a",
}) => {
  return (
    <StyledFooter backgroundColor={backgroundColor}>
      <FooterContainer>
        {!compact && <Divider style={{ opacity: 0.5 }} />}

        <FooterSection>
          {/* 1/3 - Legal Links */}
          <div>
          <LegalLinks>
              {legalLinks.map((legal, index) =>
                legal.external ? (
                  <a
                    key={index}
                    href={legal.path}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography variant="caption" colorScheme="muted" as="span">
                      {legal.label}
                    </Typography>
                  </a>
                ) : (
                  <LinkComponent
                    key={index}
                    {...(LinkComponent === "a"
                      ? { href: legal.path }
                      : { to: legal.path })}
                    style={{
                      color: "inherit",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    <Typography variant="caption" colorScheme="muted" as="span">
                      {legal.label}
                    </Typography>
                  </LinkComponent>
                )
              )}
            </LegalLinks>
          </div>

          {/* 2/3 - Social Icons */}
          <div>
            {socialLinks.length > 0 && (
              <SocialIcons>
                {socialLinks.map((social, index) => (
                  <Button
                    key={index}
                    variant="flat"
                    size="fit"
                    onClick={() => window.open(social.url, "_blank")}
                    aria-label={`Visit our ${social.url} page`}
                  >
                    {social.icon}
                  </Button>
                ))}
              </SocialIcons>
            )}
          </div>

          {/* 3/3 - Copyright */}
          <div>
            {showCopyright && (
              <Typography variant="caption" colorScheme="muted" as="span" white-space="nowrap">
              © {new Date().getFullYear()} {title}
              </Typography>
            )}
          </div>
        </FooterSection>
      </FooterContainer>
    </StyledFooter>
  );
};